/**
 * Portal Preview Sheet
 *
 * Slides in from the right to show a preview of what external parties
 * (title/escrow or borrower) see in the conditions portal.
 * Uses the actual audience-specific section components with no-op handlers.
 *
 * Conditions are filtered by audience:
 * - Title/Escrow: Only conditions explicitly assigned via condition_title_escrow_assignments
 * - Borrower: All conditions EXCEPT those assigned to title/escrow
 */

import { useMemo } from 'react';
import { Eye, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PortalHeader } from './PortalHeader';
import { LoanSummaryCard } from './LoanSummaryCard';
import { ContactCards } from './ContactCards';
import { TitleEscrowConditionsSection } from './TitleEscrowConditionsSection';
import { BorrowerConditionsPreview } from './BorrowerConditionsPreview';
import type {
  ConditionsPortalLoanData,
  ConditionsPortalLoanOfficer,
  ConditionsPortalCompany,
  ConditionTemplateInfo,
  ConditionsPortalUpload
} from '@/types/conditions-portal';
import type { EncompassCondition } from '@/types/approval-letter';

interface PortalPreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portalType: 'title_escrow' | 'borrower';
  loanData: ConditionsPortalLoanData;
  loanOfficer: ConditionsPortalLoanOfficer;
  company: ConditionsPortalCompany;
  templates: Record<string, ConditionTemplateInfo>;
  uploads: ConditionsPortalUpload[];
  conditionNotes?: Record<string, string>;
  titleEscrowAssignedIds: Set<string>;
  titleEscrowNotes: Record<string, string>;
  borrowerAssignedIds: Set<string>;
  borrowerNotes: Record<string, string>;
  documentsNeededBy?: string | null;
}

const noop = () => {};

/**
 * Check if a condition matches an ID in the assigned set.
 * Conditions may use externalId, id, or internalId as identifiers.
 */
const isConditionAssigned = (c: EncompassCondition, assignedIds: Set<string>): boolean =>
  (!!c.externalId && assignedIds.has(c.externalId)) ||
  (!!c.id && assignedIds.has(c.id)) ||
  (!!c.internalId && assignedIds.has(c.internalId));

export const PortalPreviewSheet = ({
  open,
  onOpenChange,
  portalType,
  loanData,
  loanOfficer,
  company,
  templates,
  uploads,
  conditionNotes,
  titleEscrowAssignedIds,
  titleEscrowNotes,
  borrowerAssignedIds,
  borrowerNotes,
  documentsNeededBy
}: PortalPreviewSheetProps) => {
  const isTitleEscrow = portalType === 'title_escrow';
  const title = isTitleEscrow ? 'Title/Escrow Portal' : 'Borrower Portal';

  // Filter conditions based on portal type
  // Title/Escrow: only explicitly assigned conditions (timing-grouped)
  const filteredConditions = useMemo(() => {
    const filterFn = (c: EncompassCondition) => isConditionAssigned(c, titleEscrowAssignedIds);
    return {
      ptd: loanData.conditions.ptd.filter(filterFn),
      ptf: loanData.conditions.ptf.filter(filterFn),
      ac: loanData.conditions.ac.filter(filterFn)
    };
  }, [loanData.conditions, titleEscrowAssignedIds]);

  // Borrower: flat list of explicitly assigned conditions (status-grouped by BorrowerConditionsPreview)
  const borrowerConditions = useMemo(() => {
    if (isTitleEscrow) {
return [];
}
    const all = [
      ...loanData.conditions.ptd,
      ...loanData.conditions.ptf,
      ...loanData.conditions.ac
    ];
    return all.filter(c => isConditionAssigned(c, borrowerAssignedIds));
  }, [loanData.conditions, isTitleEscrow, borrowerAssignedIds]);

  const allFiltered = isTitleEscrow
    ? [...filteredConditions.ptd, ...filteredConditions.ptf, ...filteredConditions.ac]
    : borrowerConditions;
  const hasConditions = allFiltered.length > 0;

  // Merge notes: title/escrow assignment notes + any existing condition notes
  const mergedNotes = useMemo(() => {
    if (!isTitleEscrow) {
return { ...conditionNotes, ...borrowerNotes };
}
    return { ...conditionNotes, ...titleEscrowNotes };
  }, [isTitleEscrow, conditionNotes, titleEscrowNotes, borrowerNotes]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="!w-[60vw] !max-w-none sm:!max-w-none p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-slate-500" />
            <div>
              <SheetTitle className="text-lg">{title} Preview</SheetTitle>
              <SheetDescription>
                Preview of what {isTitleEscrow ? 'title/escrow companies' : 'borrowers'} see
              </SheetDescription>
            </div>
            <Badge variant="secondary" className="ml-auto bg-blue-100 text-blue-700 text-xs">
              Preview Mode
            </Badge>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)]">
          <div className="bg-slate-50">
            {/* Title/Escrow Preview */}
            {isTitleEscrow && (
              <div className="space-y-0">
                <PortalHeader company={company} />

                <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
                  <LoanSummaryCard loanData={loanData} documentsNeededBy={documentsNeededBy} />
                  <ContactCards
                    loanOfficer={loanOfficer}
                    processor={loanData.processor || null}
                  />

                  {/* Conditions by timing */}
                  {hasConditions ? (
                    <div className="space-y-6">
                      {filteredConditions.ptd.length > 0 && (
                        <TitleEscrowConditionsSection
                          title="Prior to Docs"
                          conditions={filteredConditions.ptd}
                          templates={templates}
                          uploads={uploads}
                          notes={mergedNotes}
                          onDownloadTemplate={noop}
                          onUploadClick={noop}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      )}
                      {filteredConditions.ptf.length > 0 && (
                        <TitleEscrowConditionsSection
                          title="Prior to Funding"
                          conditions={filteredConditions.ptf}
                          templates={templates}
                          uploads={uploads}
                          notes={mergedNotes}
                          onDownloadTemplate={noop}
                          onUploadClick={noop}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      )}
                      {filteredConditions.ac.length > 0 && (
                        <TitleEscrowConditionsSection
                          title="At Closing"
                          conditions={filteredConditions.ac}
                          templates={templates}
                          uploads={uploads}
                          notes={mergedNotes}
                          onDownloadTemplate={noop}
                          onUploadClick={noop}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      )}
                    </div>
                  ) : (
                    <EmptyConditionsState
                      message="No conditions assigned to Title/Escrow"
                      hint="Use 'Assign Conditions' → 'Route to Title & Escrow' to assign conditions."
                    />
                  )}
                </div>
              </div>
            )}

            {/* Borrower Preview — status-grouped */}
            {!isTitleEscrow && (
              <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
                <ContactCards
                  loanOfficer={loanOfficer}
                  processor={loanData.processor || null}
                />
                <BorrowerConditionsPreview
                  conditions={borrowerConditions}
                  uploads={uploads}
                  templates={templates}
                  conditionNotes={mergedNotes}
                  borrowerFirstName={loanData.borrower.firstName || loanData.borrower.name.split(' ')[0]}
                />
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

const EmptyConditionsState = ({ message, hint }: { message: string; hint: string }) => (
  <div className="text-center py-12">
    <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
    <p className="text-slate-600 font-medium">{message}</p>
    <p className="text-sm text-slate-400 mt-1">{hint}</p>
  </div>
);
